"use client"

import { Cookies } from "@/config/enums"
import type { TLoginUserData } from "@/schemas/user.schema"
import { useAppStoreContext } from "@/stores/appStore"
import { hydrateSyncStoreFromInitialUserData, initializeTwilioSyncClient } from "@/sync/sync"
import { useEmbeddedEvents } from "@/hooks/useEmbeddedEvents"
import { useIntercom } from "@/hooks/useIntercom"
import { usePathname } from "next/navigation"
import { useFullStory } from "@/hooks/useFullStory"
import { useLogout } from "@/hooks/useLogout"
import { useSelector } from "@legendapp/state/react"
import { syncStore$ } from "@/stores/sync.v2.store"
import { isPublicSubscribePath } from "@/app/subscribe/utils"
import { buildFrontUrl } from "@/lib/front-url"
import React, { useEffect, useLayoutEffect, useRef } from "react"

interface ClientRuntimeEffectsProps {
  userData?: TLoginUserData
  intercomUserHash?: string
}

function getCookie(cName: string): string {
  const name = cName + "="
  const cDecoded = decodeURIComponent(document.cookie)
  const cArr = cDecoded.split("; ")

  return cArr.reduce((accumulator, currentValue) => {
    if (accumulator.length > 0) return accumulator

    if (currentValue.indexOf(name) === 0) return currentValue.substring(name.length)

    return accumulator
  }, "")
}

export function ClientRuntimeEffects({ userData, intercomUserHash }: ClientRuntimeEffectsProps) {
  const pathname = usePathname()
  const { setUserData, changeSidebarMenuState } = useAppStoreContext((s) => s)
  const { isReady: isFullStoryReady, setIdentity } = useFullStory()
  const { isReady: isIntercomReady, initialize, shutdown } = useIntercom()
  const { performLogout } = useLogout()
  const lastUserID = useRef<number | null | undefined>(null)
  const lastClusterID = useRef<string | undefined>(undefined)
  const clusterID = useSelector(() => syncStore$.account.clusterId.get())

  useEmbeddedEvents()

  useLayoutEffect(() => {
    if (!userData || userData.account.status === "LOGGED_OUT") {
      return
    }

    setUserData(userData)
    hydrateSyncStoreFromInitialUserData(userData)
    lastClusterID.current = userData.account.clusterId
  }, [setUserData, userData])

  useEffect(() => {
    changeSidebarMenuState(false)

    const checkAuthCookie = () => {
      const launcherIDCookie = getCookie(Cookies.Enum["launcher-id"])
      const hasUpstreamSessionCookies = launcherIDCookie.length > 0
      const isPublicSubscribeRoute = pathname ? isPublicSubscribePath(pathname) : false

      if ((!pathname || (!pathname.startsWith("/login") && !pathname.startsWith("/recover-password") && !pathname.startsWith("/sign-up") && !isPublicSubscribeRoute)) && !hasUpstreamSessionCookies) {
        const loginFullURL = buildFrontUrl("/login", {
          frontUrl: process.env.NEXT_PUBLIC_FRONT_URL,
          fallbackOrigin: window.location.origin,
        })
        window.location.replace(loginFullURL)
      }
    }

    checkAuthCookie()

    const intervalChecker = setInterval(checkAuthCookie, 5 * 1_000)
    window.addEventListener("focus", checkAuthCookie)
    document.addEventListener("visibilitychange", checkAuthCookie)

    return () => {
      clearInterval(intervalChecker)
      window.removeEventListener("focus", checkAuthCookie)
      document.removeEventListener("visibilitychange", checkAuthCookie)
    }
  }, [changeSidebarMenuState, pathname])

  useEffect(() => {
    if (!isFullStoryReady) {
      return
    }

    if (userData && userData.account.status !== "LOGGED_OUT") {
      setIdentity({
        uid: userData.user.id.toString(),
        properties: {
          displayName: `${userData.user.name} ${userData.user.lastName}`,
          email: userData.user.email,
          companyName: userData.user.companyName,
          account: userData.user.accountId,
          status: userData.user.accountState,
          seats: userData.user.accountSeats,
          signupChannel: userData.user.accountSignupChannel,
          signupPlan: userData.user.accountSignupPlan,
          smsOptIn: userData.user.smsOptIn,
          emailMarketingOptIn: userData.user.emailMarketingOptIn,
        },
      })
      return
    }

    setIdentity({
      anonymous: true,
    })
  }, [isFullStoryReady, setIdentity, userData])

  useEffect(() => {
    if (!userData) {
      return
    }

    if (userData.user.id === lastUserID.current) {
      return
    }

    lastUserID.current = userData.user.id

    if (userData.account.status !== "LOGGED_OUT") {
      void initializeTwilioSyncClient({
        initialUserData: userData,
        token: userData.twilio.syncToken,
      }).catch(() => {})

      if (!isIntercomReady) {
        initialize({
          userData,
          userHash: intercomUserHash,
        })
      }

      return
    }

    if (isIntercomReady) {
      shutdown()
    }
  }, [initialize, intercomUserHash, isIntercomReady, shutdown, userData])

  useEffect(() => {
    if (!clusterID || !lastClusterID.current) {
      lastClusterID.current = clusterID || undefined
      return
    }

    if (clusterID !== lastClusterID.current) {
      performLogout()
      return
    }

    lastClusterID.current = clusterID
  }, [clusterID, performLogout])

  return null
}
